/* modal-a11y.js
   ---------------------------------------------------------------------------
   Focus management for the Showcase theme's `.theme-modal` dialogs.

   theme.js already declares role="dialog" + aria-modal="true" and moves focus
   to the close button on open, but it implements no focus trap, no Escape
   handler, and no focus restore. So a keyboard user Tabs straight out of the
   dialog into the page behind it (which is still there and still reachable),
   cannot dismiss without finding the X, and is dumped at the top of the
   document afterwards.

   This lives outside theme.js on purpose: theme.js is vendor code from
   Showcase 10.1.0 and gets replaced on theme updates. This file hooks the
   `.reveal` class the theme already toggles, so it needs no theme.js changes
   and survives an update.

   Covers every `.theme-modal`: quick buy, added-to-cart, #search-modal, and
   the mobile #page-menu nav.

   WCAG 2.1.2 (A) No Keyboard Trap, 2.4.3 (A) Focus Order, 2.1.1 (A) Keyboard.
   See ACCESSIBILITY.md Hard Rule 7.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var lastOutsideFocus = null;
  var lastOpenModal = null;
  var wasOpen = false;

  function openModal() {
    return document.querySelector('.theme-modal.reveal');
  }

  /* Any open dialog, including ones the theme does not own: the gold basketball
     gallery lightbox is a hand-rolled role="dialog" toggled by the `hidden`
     attribute. It already handles Escape, arrow keys, and focus restore itself,
     but it has no Tab trap, so only the trap below applies to it. Escape and
     the open-focus pull stay scoped to .theme-modal so we do not fight a
     dialog that already manages those. */
  function openDialog() {
    var themeModal = openModal();
    if (themeModal) return themeModal;
    var dialogs = document.querySelectorAll('[role="dialog"]:not([hidden])');
    for (var i = 0; i < dialogs.length; i++) {
      var d = dialogs[i];
      if (d.offsetWidth > 0 || d.offsetHeight > 0) return d;
    }
    return null;
  }

  /* The theme parks tabindex="-1" on modal links while closed and only clears
     it on open, so filter on what is actually reachable right now. */
  function focusables(modal) {
    var all = modal.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.getAttribute('tabindex') === '-1') continue;
      if (el.offsetWidth === 0 && el.offsetHeight === 0 && !el.getAttribute('aria-label')) continue;
      out.push(el);
    }
    return out;
  }

  /* Remember where focus was before a dialog took it, so it can go back, and
     keep focus inside an open dialog.

     Containment lives here rather than on the open transition because the
     theme opens dialogs through two different code paths with different
     focus behavior, and a focusin listener catches every way focus can end up
     behind an open dialog: the trigger keeping it, a stray programmatic
     focus(), or Tab in a browser that skips our keydown handler. */
  document.addEventListener('focusin', function (e) {
    var inModal = e.target.closest && e.target.closest('.theme-modal');
    if (inModal) return;

    var modal = openModal();
    if (modal) {
      pullFocusInto(modal);
      return; // do not record a focus that belongs behind the dialog
    }

    lastOutsideFocus = e.target;
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      // Only theme modals need us to close them; other dialogs bring their own
      // Escape handler and calling closeThemeModal on them would stomp state.
      if (openModal() && typeof window.closeThemeModal === 'function') {
        e.preventDefault();
        window.closeThemeModal();
      }
      return;
    }

    if (e.key !== 'Tab' && e.keyCode !== 9) return;

    var modal = openDialog();
    if (!modal) return;

    var items = focusables(modal);
    if (!items.length) {
      // Nothing tabbable inside: keep focus on the dialog rather than letting
      // it escape to the page behind.
      e.preventDefault();
      modal.focus();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    // Focus sitting on the dialog container itself counts as "before first".
    if (e.shiftKey && (active === first || active === modal || !modal.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    } else if (!modal.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  });

  function pullFocusInto(modal) {
    var items = focusables(modal);
    var target = items[0];
    if (!target) {
      if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1');
      target = modal;
    }
    try { target.focus({ preventScroll: true }); } catch (e) {}
  }

  /* Pull focus in on open, and restore it on close whichever way the dialog was
     closed: Escape, the X, or a click on the backdrop. Watching the class is
     more reliable than wrapping closeThemeModal, which theme.js may reassign
     after this file runs.

     The open case cannot be handled by the focusin listener above: if the
     trigger already holds focus when the dialog opens, focus never *changes*,
     so no focusin fires and focus is simply left behind the dialog. */
  if (window.MutationObserver) {
    new MutationObserver(function () {
      var modal = openModal();
      var isOpen = !!modal;

      if (!wasOpen && isOpen) {
        var active = document.activeElement;
        // Focus on the container itself counts as "not yet on a control":
        // modal.contains(modal) is true, so test for a real descendant.
        if (!(active && active !== modal && modal.contains(active))) {
          // Retry rather than bet on a single moment: the reveal transition and
          // the theme's own focus calls land at slightly different times, and
          // controls inside can still be display:none on the first attempt.
          [350, 700].forEach(function (delay) {
            setTimeout(function () {
              if (!modal.classList.contains('reveal')) return;
              var a = document.activeElement;
              if (a && a !== modal && modal.contains(a)) return;
              pullFocusInto(modal);
            }, delay);
          });
        }
      }

      if (wasOpen && !isOpen && lastOutsideFocus) {
        // theme.js runs its own 300ms teardown timers on close, which can leave
        // focus on <body> or stranded on a control inside the now-hidden
        // dialog. Restore after those, and re-assert in case a later timer
        // blurs again.
        var restoreTo = lastOutsideFocus;
        var closed = lastOpenModal;
        [0, 350, 700].forEach(function (delay) {
          setTimeout(function () {
            if (openModal()) return;                       // reopened meanwhile
            if (!document.contains(restoreTo)) return;
            var active = document.activeElement;
            if (active === restoreTo) return;
            // Correct focus only when it was dropped or left inside the closed
            // dialog. Never steal it from a control the user has moved to.
            var stranded = active === document.body ||
                           active === document.documentElement ||
                           (closed && closed.contains(active));
            if (!stranded) return;
            try { restoreTo.focus({ preventScroll: true }); } catch (e) {}
          }, delay);
        });
      }
      if (isOpen) lastOpenModal = modal;
      wasOpen = isOpen;
    }).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }
})();
